import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { listProjects, listServiceRequests } from '../lib/adminApi';
import { CANVAS_REGISTRY } from '../canvases/registry';
import { campaignUrl } from '../lib/siteUrl';
import { formatSgd } from '../lib/pricing';

function StatusBadge({ status }) {
  return <span className={`admin-badge admin-badge-${status}`}>{status}</span>;
}

function ServiceTable({ title, rows, onSelect, emptyText }) {
  return (
    <div className="admin-card">
      <h2>{title}</h2>
      <div className="admin-table-wrap" style={{ border: 'none' }}>
        <table className="admin-table">
          <thead>
            <tr>
              <th>Customer</th>
              <th>Domain</th>
              <th>Canvas</th>
              <th>Status</th>
              <th>Start</th>
              <th>End</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((p) => (
              <tr key={p.id} className="clickable" onClick={() => onSelect(p)}>
                <td>{p.billings?.customer_name || '—'}</td>
                <td>{p.domain_name}</td>
                <td>{CANVAS_REGISTRY.find((c) => c.key === p.canvas_type)?.label || p.canvas_type}</td>
                <td><StatusBadge status={p.status} /></td>
                <td>{new Date(p.start_date).toLocaleDateString()}</td>
                <td>{new Date(p.end_date).toLocaleDateString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length === 0 && <div className="admin-empty">{emptyText}</div>}
      </div>
    </div>
  );
}

export default function Services() {
  const [projects, setProjects] = useState([]);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([listProjects(), listServiceRequests().catch(() => ({ requests: [] }))])
      .then(([p, r]) => {
        setProjects(p.projects || []);
        setPending(r.requests || []);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  const active = useMemo(() => projects.filter((p) => p.status !== 'expired'), [projects]);
  const historical = useMemo(() => projects.filter((p) => p.status === 'expired'), [projects]);

  const openPending = (req) => {
    navigate('/admin/billings', { state: { wizard: true, pendingRequest: req } });
  };

  if (selected) {
    const billing = selected.billings || {};
    return (
      <div>
        <button className="admin-back-link" onClick={() => setSelected(null)}>
          Back to Service Homepage
        </button>
        <div className="admin-page-header">
          <div>
            <h1>{billing.customer_name || 'Service'}</h1>
            <p><a href={campaignUrl(selected.domain_name)} target="_blank" rel="noreferrer">{campaignUrl(selected.domain_name)}</a></p>
          </div>
          <StatusBadge status={selected.status} />
        </div>
        <div className="admin-card">
          <div className="admin-detail-grid">
            <div className="admin-detail-item">
              <div className="admin-detail-label">Organisation</div>
              <div className="admin-detail-value">{billing.customer_organisation || '—'}</div>
            </div>
            <div className="admin-detail-item">
              <div className="admin-detail-label">Email</div>
              <div className="admin-detail-value">{billing.customer_email || '—'}</div>
            </div>
            <div className="admin-detail-item">
              <div className="admin-detail-label">Canvas</div>
              <div className="admin-detail-value">
                {CANVAS_REGISTRY.find((c) => c.key === selected.canvas_type)?.label || selected.canvas_type}
              </div>
            </div>
            <div className="admin-detail-item">
              <div className="admin-detail-label">Activation Code</div>
              <div className="admin-detail-value"><span className="admin-code">{selected.activation_code}</span></div>
            </div>
            <div className="admin-detail-item">
              <div className="admin-detail-label">Start Date</div>
              <div className="admin-detail-value">{new Date(selected.start_date).toLocaleString()}</div>
            </div>
            <div className="admin-detail-item">
              <div className="admin-detail-label">End Date (effective)</div>
              <div className="admin-detail-value">{new Date(selected.end_date).toLocaleString()}</div>
            </div>
            <div className="admin-detail-item">
              <div className="admin-detail-label">Activated by customer</div>
              <div className="admin-detail-value">{selected.is_activated ? 'Yes' : 'Not yet'}</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="admin-page-header">
        <div>
          <h1>Services</h1>
          <p>Every customer campaign, pending activation, active or historical.</p>
        </div>
        <button
          className="admin-btn admin-btn-primary"
          onClick={() => navigate('/admin/billings', { state: { wizard: true } })}
        >
          + Create New Service
        </button>
      </div>

      {error && <div className="admin-alert admin-alert-error">{error}</div>}

      {!loading && (
        <>
          <div className="admin-card">
            <h2>Services Pending Activation</h2>
            <div className="admin-table-wrap" style={{ border: 'none' }}>
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Customer</th>
                    <th>Organisation</th>
                    <th>Email</th>
                    <th>Canvas</th>
                    <th>Fee</th>
                    <th>Start</th>
                    <th>End</th>
                  </tr>
                </thead>
                <tbody>
                  {pending.map((req) => (
                    <tr key={req.id} className="clickable" onClick={() => openPending(req)}>
                      <td>{req.customer_name}</td>
                      <td>{req.customer_organisation || '—'}</td>
                      <td>{req.customer_email}</td>
                      <td>{req.canvas_library?.label || req.moving_object_type || '—'}</td>
                      <td>{formatSgd(req.billing_amount)}</td>
                      <td>{req.start_date}</td>
                      <td>{req.end_date}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {pending.length === 0 && <div className="admin-empty">No services waiting for activation.</div>}
            </div>
          </div>
          <ServiceTable title="Current Active Services" rows={active} onSelect={setSelected} emptyText="No active services yet." />
          <ServiceTable title="Historical Services" rows={historical} onSelect={setSelected} emptyText="No historical services yet." />
        </>
      )}
    </div>
  );
}
